import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, currentMonthISO } from "../lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Calculator, Ban } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

const emptyForm = {
  codigo: "",
  nombre: "",
  categoria: "muebles",
  fecha_adquisicion: new Date().toISOString().slice(0, 10),
  valor: "",
  valor_residual: "0",
  metodo: "normal",
  vida_util_anos: "",
  observaciones: "",
};

export default function ActivoFijo() {
  const [activos, setActivos] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [open, setOpen] = useState(false);
  const [openDep, setOpenDep] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [periodo, setPeriodo] = useState(currentMonthISO());
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/activo-fijo"),
        api.get("/activo-fijo/catalogo-vidas"),
      ]);
      setActivos(a.data);
      setCatalogo(c.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const page = useClientPagination(activos, `${activos.length}`);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/activo-fijo", {
        ...form,
        valor: parseFloat(form.valor) || 0,
        valor_residual: parseFloat(form.valor_residual) || 0,
        vida_util_anos: form.vida_util_anos ? parseFloat(form.vida_util_anos) : null,
      });
      toast.success("Activo registrado");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const depreciar = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/activo-fijo/depreciar-mes", { periodo });
      toast.success(
        `Depreciación ${periodo}: ${fmtCLP(data.total)} · ${data.activos_depreciados?.length || 0} bienes · borrador en Libro diario`
      );
      setOpenDep(false);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const baja = async (id) => {
    if (!window.confirm("¿Dar de baja este activo? Dejará de depreciarse.")) return;
    try {
      await api.post(`/activo-fijo/${id}/baja`);
      toast.success("Activo dado de baja");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este activo (sin depreciación previa)?")) return;
    try {
      await api.delete(`/activo-fijo/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const catLabel = (id) => catalogo.find((c) => c.id === id)?.label || id;

  return (
    <Page>
      <PageHeader
        title="Activo fijo"
        subtitle="Fichas de bienes y depreciación mensual (normal o acelerada tributaria)"
        testid="af-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Dialog open={openDep} onOpenChange={setOpenDep}>
              <DialogTrigger asChild>
                <button
                  data-testid="af-depreciar"
                  className="inline-flex items-center gap-1.5 border border-warning-foreground text-warning-foreground hover:bg-warning/15 text-sm px-3 py-1.5 rounded-sm"
                >
                  <Calculator className="h-4 w-4" /> Depreciar mes
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Depreciación del mes</DialogTitle>
                  <DialogDescription>
                    Genera un asiento en borrador (gasto depreciación / depreciación acumulada). Autorícelo en el Libro diario.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={depreciar} className="space-y-3">
                  <label className="block text-sm">
                    Período
                    <input
                      type="month"
                      required
                      value={periodo}
                      onChange={(e) => setPeriodo(e.target.value)}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm"
                      data-testid="af-periodo"
                    />
                  </label>
                  <DialogFooter>
                    <button type="submit" className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm">
                      Generar asiento
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="af-nuevo"
                  className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm"
                >
                  <Plus className="h-4 w-4" /> Nuevo activo
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Alta de activo fijo</DialogTitle>
                  <DialogDescription>Registra el bien para depreciarlo mes a mes.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">
                      Código
                      <input required value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="af-codigo" />
                    </label>
                    <label className="block text-sm">
                      Fecha adquisición
                      <input type="date" required value={form.fecha_adquisicion}
                        onChange={(e) => setForm({ ...form, fecha_adquisicion: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                  </div>
                  <label className="block text-sm">
                    Nombre
                    <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="af-nombre" />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">
                      Categoría
                      <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {catalogo.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.label} ({c.anos_normal} años)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                    <label className="block text-sm">
                      Método
                      <Select value={form.metodo} onValueChange={(v) => setForm({ ...form, metodo: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="acelerada">Acelerada (⅓ vida)</SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <label className="block text-sm">
                      Valor
                      <input type="number" required min="1" value={form.valor}
                        onChange={(e) => setForm({ ...form, valor: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="af-valor" />
                    </label>
                    <label className="block text-sm">
                      Residual
                      <input type="number" min="0" value={form.valor_residual}
                        onChange={(e) => setForm({ ...form, valor_residual: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                    <label className="block text-sm">
                      Vida (años, opcional)
                      <input type="number" min="1" step="0.1" value={form.vida_util_anos}
                        onChange={(e) => setForm({ ...form, vida_util_anos: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm"
                        placeholder="Catálogo" />
                    </label>
                  </div>
                  <DialogFooter>
                    <button type="submit" className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm" data-testid="af-guardar">
                      Guardar
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {loading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
      ) : (
        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <table className="acc-table" data-testid="af-tabla">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Categoría</th>
                <th>Adquisición</th>
                <th className="num">Valor</th>
                <th className="num">Cuota mes</th>
                <th className="num">Acumulada</th>
                <th className="num">Neto</th>
                <th>Método</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {page.pageItems.length === 0 ? (
                <tr><td colSpan={11} className="text-center text-muted-foreground text-sm py-8">Sin activos. Registra el primero.</td></tr>
              ) : page.pageItems.map((a) => (
                <tr key={a.id} className={a.estado === "baja" ? "opacity-60" : ""}>
                  <td className="font-mono text-[12px]">{a.codigo}</td>
                  <td className="font-medium">{a.nombre}</td>
                  <td className="text-[12px]">{catLabel(a.categoria)}</td>
                  <td>{fmtDate(a.fecha_adquisicion)}</td>
                  <td className="num">{fmtCLP(a.valor)}</td>
                  <td className="num">{fmtCLP(a.cuota_mensual)}</td>
                  <td className="num">{fmtCLP(a.depreciacion_acumulada)}</td>
                  <td className="num font-semibold">{fmtCLP(a.valor_neto)}</td>
                  <td className="text-[11px] uppercase">{a.metodo}</td>
                  <td className="text-[11px] uppercase">{a.estado}{a.ultima_depreciacion ? ` · ${a.ultima_depreciacion}` : ""}</td>
                  <td>
                    <div className="flex gap-0.5">
                      {a.estado === "activo" && (
                        <button onClick={() => baja(a.id)} title="Dar de baja" className="text-warning-foreground hover:bg-warning/15 p-1 rounded-sm">
                          <Ban className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {(a.depreciacion_acumulada || 0) === 0 && !a.ultima_depreciacion && (
                        <button onClick={() => eliminar(a.id)} title="Eliminar" className="text-destructive hover:bg-destructive/10 p-1 rounded-sm">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <LibroPagination
            pagination={page.pagination}
            pageSize={page.pageSize}
            onPageSizeChange={page.setPageSize}
            onPageChange={page.setPage}
            testidPrefix="af"
          />
        </div>
      )}
    </Page>
  );
}
